/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import * as actionTypes from '../constants/action-types';
import UniversalTrackerModalService from '../model/UniversalTrackerModalService';
import { apiClient, handleTokenAuth } from './api';
import { API } from '../constants/errors';
import { createUniversalTrackerValue } from './universalTrackerValue';
import { handleError } from './error';
import { UtrvType } from '../constants/status';
import UniversalTracker from '../model/UniversalTracker';
import { ValueData } from '../types/universalTrackerValue';
import { AppDispatch, RootState } from '../reducers';
import { NewUniversalTrackerValuePlain } from '../model/UniversalTrackerValue';
import { withProfile } from '../utils/files';
import { DataFormState } from '@features/utr-modal/components/SetDataForm';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { DataPeriods } from '@utils/dataPeriods';
import G17Client from '@services/G17Client';
import { UniversalTrackerType } from '../types/universalTracker';
import { UniversalTrackerModalShowAs } from '@g17eco/types/universalTrackerModal';

interface UniversalTrackerModalProps {
  universalTrackerId: string;
  universalTrackerType?: UniversalTrackerType | undefined;
  actionValueId?: string | undefined;
  params?: { surveyId: string };
  openAction?: { activeTabId?: UniversalTrackerModalShowAs, hideProvenance?: boolean };
  alternativeCode?: string;
}

interface FormData {
  value: any;
  effectiveDate?: string;
  metricUnit?: any;
  type?: UtrvType;
  universalTracker?: UniversalTracker;
  files?: any[];
  sampleSize?: number | boolean;
  valueData?: ValueData;
  note?: string;
  period?: DataPeriods;
}

export function loadUniversalTrackerModal(props: UniversalTrackerModalProps) {
  const {
    universalTrackerId,
    universalTrackerType = UniversalTrackerType.Utr,
    actionValueId,
    params,
    openAction,
    alternativeCode,
  } = props;
  return (dispatch: AppDispatch, getState: () => RootState) => {
    if (!universalTrackerId) {
      return;
    }
    const modalState = getState().universalTrackerModal;
    const isSameDispatch = modalState.universalTrackerId === universalTrackerId &&
      modalState.universalTrackerType === universalTrackerType;

    if (isSameDispatch) {
      if (modalState.loadedOverview && !modalState.open) {
        dispatch({
          ...openAction,
          type: actionTypes.OPEN_UNIVERSALTRACKER_MODAL,
          alternativeCode,
        });
      }
      return;
    }

    dispatch({
      ...openAction,
      type: actionTypes.LOADING_UNIVERSALTRACKER_MODAL,
      universalTrackerType: universalTrackerType,
      universalTrackerId: universalTrackerId,
      alternativeCode,
    });

    const options = handleTokenAuth(getState);
    if (params) {
      options.params = params;
    }
    let apiRequest;
    // If we have utrv id, fetch extended data
    if (actionValueId) {
      apiRequest = apiClient.get('universal-tracker-values/' + actionValueId + '/modal/info', options)
    } else {
      apiRequest = apiClient.get('universal-trackers/' + universalTrackerId + '/modal/info', options);
    }

    apiRequest
      .then(response => {
        const modalService: UniversalTrackerModalService = new UniversalTrackerModalService(
          response.data.data,
          universalTrackerType
        );
        if (actionValueId) {
          modalService.setCurrentValueId(actionValueId);

          // Set up extra data
          if (universalTrackerType === 'utr') {
            const utrvData = response.data.data.universalTrackerValues;
            if (utrvData.length === 1) {
              modalService.setCurrentValue(utrvData[0]);
              modalService.setInitiativeId(utrvData[0].initiativeId);
            }
          }
        }

        if (!modalService.getInitiativeId()) {
          modalService.setInitiativeId(getState().initiative.data?._id);
        }

        dispatch(loadHistoryByModalService(modalService));
        dispatch({
          type: actionTypes.LOADED_INFO_UNIVERSALTRACKER_MODAL,
          modalService: modalService
        });

      })
      .catch(handleError(dispatch, actionTypes.ERROR_UNIVERSALTRACKER_MODAL));
  }
}

export function loadHistoryByModalService(
  modalService: UniversalTrackerModalService,
  filters?: { surveyType?: SurveyType; period?: DataPeriods }
) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const modalState = getState().universalTrackerModal;
    if (modalState.loadingHistory || modalService.universalTrackerValuesLoaded()) {
      return;
    }

    dispatch({ type: actionTypes.LOADING_HISTORY_UNIVERSALTRACKER_MODAL });

    const initiativeId: string | undefined = modalService.getInitiativeId() || getState().initiative.data?._id;
    if (initiativeId === undefined) {
      return;
    }

    const universalTracker = modalService.getUniversalTracker();

    const options = handleTokenAuth(getState);

    G17Client.getInitiativeUtrHistory(initiativeId, universalTracker.getId(), {
      ...options,
      params: filters,
    })
      .then((data) => {
        modalService.setUniversalTrackerValues(data);
        dispatch({
          type: actionTypes.LOADED_HISTORY_UNIVERSALTRACKER_MODAL,
          initiativeId,
          modalService,
          data,
        });
      })
      .catch(handleError(dispatch, actionTypes.ERROR_UNIVERSALTRACKER_MODAL));
  };
}

export function reloadHistory(
  modalService: UniversalTrackerModalService,
  filters: { surveyType?: SurveyType; period?: DataPeriods }
) {
  modalService.setUniversalTrackerValues(undefined); // Clear cache
  return loadHistoryByModalService(modalService, filters);
}

export function closeUniversalTrackerModal() {
  return (dispatch: AppDispatch) => dispatch({ type: actionTypes.CLOSE_UNIVERSALTRACKER_MODAL })
}

function getFormData(form: DataFormState, additionalFields = {}) {
  // Merge additional fields first, then convert to formData
  const formData: FormData = {
    ...additionalFields,
    value: form.value,
    valueData: form.valueData
  };

  if ([UtrvType.Baseline, UtrvType.Target].includes(form.type)) {
    formData.type = form.type;
  }

  if (form.files && form.files.length > 0) {
    formData.files = [];
    form.files.forEach((file: any) => {
      formData.files?.push(file.file);
    });
  }

  if (form.sampleSize !== undefined) {
    formData.sampleSize = form.sampleSize;
  }

  if (form.comments !== undefined) {
    formData.note = form.comments;
  }

  if (form.effectiveDate !== undefined) {
    formData.effectiveDate = form.effectiveDate;
  }

  if (form.period !== undefined) {
    formData.period = form.period;
  }

  return withProfile(formData);
}

export function createUtrvByModalService(form: DataFormState, universalTrackerValue: NewUniversalTrackerValuePlain) {
  const formData = getFormData(form, universalTrackerValue);

  return async (dispatch: AppDispatch, getState: () => RootState) => {

    const saveWrapper = createUniversalTrackerValue(formData);
    const savePromise = saveWrapper(dispatch, getState);

    return savePromise.then((result) => {

      // TODO - make this only update one action rather than full refresh...
      // dispatch(loadUserActions());
      // dispatch(reloadScorecards());
      dispatch({ type: actionTypes.CLOSE_UNIVERSALTRACKER_MODAL });

      return result;
    }).catch(e => {
      console.log(e);
      throw new Error(e.response ? API.GENERIC : e.message);
    });
  }
}
